import { useEffect, useRef } from "react";

const IDLE_MS = 2 * 60 * 60 * 1000; // 2 hours
const CHECK_MS = 60 * 1000; // check every minute
const STORAGE_KEY = "stock_last_activity";
const EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];

/**
 * Watches user activity; if the tab is idle for more than IDLE_MS,
 * calls onIdle(). Uses localStorage so multi-tab activity is shared.
 */
export function useInactivityLogout(enabled, onIdle) {
    const onIdleRef = useRef(onIdle);
    onIdleRef.current = onIdle;

    useEffect(() => {
        if (!enabled) return;

        const bump = () => {
            try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
        };
        // seed immediately so a fresh session doesn't trigger idle
        bump();

        const check = () => {
            let last;
            try { last = Number(localStorage.getItem(STORAGE_KEY) || Date.now()); }
            catch { last = Date.now(); }
            if (Date.now() - last > IDLE_MS) {
                try { localStorage.removeItem(STORAGE_KEY); } catch {}
                onIdleRef.current?.();
            }
        };

        EVENTS.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
        const timer = setInterval(check, CHECK_MS);
        // Also check when tab becomes visible
        const onVis = () => { if (!document.hidden) check(); };
        document.addEventListener("visibilitychange", onVis);

        return () => {
            EVENTS.forEach((ev) => window.removeEventListener(ev, bump));
            clearInterval(timer);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [enabled]);
}
